import React, { useMemo } from "react";
import { components, type SingleValueProps } from "react-select";
import intlTelInput from "intl-tel-input/intlTelInputWithUtils";
import { Select, type Option } from "~/components/select";
import { useTranslation } from "react-i18next";

interface Props {
  id: string;
  label: string;
  outsideLabel: boolean;
  required: boolean;
  value: Option | null;
  onChange: (option: Option | null) => void;
  error: string | null | undefined;
}

const DIAL_CODES = Object.fromEntries(
  intlTelInput.getCountryData().map((country) => [country.iso2, country.dialCode])
);

const prioritizeCountries = (options: Option[], countries: string[]) => {
  for (const country of countries) {
    const index = options.findIndex((option) => option.value === country);
    if (index > -1) {
      const option = options[index];
      options.splice(index, 1);
      options.unshift(option);
    }
  }
};

const Flag = ({ iso2, a11yText }: { iso2: string; a11yText?: string }) => {
  // This markup is copied from the intl-tel-input dropdown that we're hiding
  return (
    <div className={`iti__flag iti__${iso2}`}>
      {a11yText && <span className="iti__a11y-text">{a11yText}</span>}
    </div>
  );
};

const Country = (option: Option) => {
  return (
    <>
      <Flag iso2={option.value.toString()} />
      {option.label}
    </>
  );
};

const SelectedCountry: React.FC<SingleValueProps<Option, false>> = (props) => {
  const option = props.data;
  return (
    <components.SingleValue {...props}>
      <Flag iso2={option.value.toString()} />
      <span>+{DIAL_CODES[option.value]}</span>
    </components.SingleValue>
  );
};

export const CountryInput = ({
  id,
  label,
  outsideLabel,
  required,
  value,
  onChange,
  error,
}: Props) => {
  const { t } = useTranslation("job_post");

  const options = useMemo(() => {
    const options = intlTelInput.getCountryData().map((country) => {
      const name = t(`countries.${country.iso2}`, { defaultValue: country.name });
      return {
        label: `${name} +${country.dialCode}`,
        value: country.iso2,
      };
    });
    prioritizeCountries(options, ["us"]);
    return options;
  }, [t]);

  return (
    <Select
      id={id}
      label={label}
      required={required}
      options={options}
      onSelect={(selected) => onChange(selected as Option | null)}
      outsideLabel={outsideLabel}
      selected={value}
      error={error}
      placeholderText={""}
      menuWidth={300}
      formatOptionLabel={Country}
      components={{ SingleValue: SelectedCountry }}
    />
  );
};
